import type {
  ChargerFilterKey,
  ChargerSortDir,
  ChargerSortKey,
  ChargerTileState,
} from "../types/charger";

const HEALTH_RANK = { stale: 0, unknown: 1, ok: 2 } as const;

function fcrRank(state: ChargerTileState): number {
  if (state.fcrActivated) return 2;
  return 0;
}

function matchesFilter(state: ChargerTileState, filters: Set<ChargerFilterKey>): boolean {
  if (!filters.size) return true;

  const checks: Record<ChargerFilterKey, boolean> = {
    ev: state.evConnected,
    chg: state.charging,
    health_ok: state.health === "ok",
    health_new: state.health === "unknown",
    health_stale: state.health === "stale",
    mv5: state.mv5,
    act: state.fcrActivated,
    sp: state.sp,
    resp: state.resp,
  };

  for (const key of filters) {
    if (!checks[key]) return false;
  }
  return true;
}

export function filterChargerStates(
  states: ChargerTileState[],
  filters: Set<ChargerFilterKey>,
): ChargerTileState[] {
  return states.filter((s) => matchesFilter(s, filters));
}

export function sortChargerStates(
  states: ChargerTileState[],
  sortKey: ChargerSortKey,
  sortDir: ChargerSortDir,
): ChargerTileState[] {
  const dir = sortDir === "asc" ? 1 : -1;
  const sorted = [...states];

  sorted.sort((a, b) => {
    let cmp = 0;
    if (sortKey === "id") {
      cmp = a.label.localeCompare(b.label);
    } else if (sortKey === "health") {
      cmp = HEALTH_RANK[a.health] - HEALTH_RANK[b.health];
    } else if (sortKey === "mv5") {
      cmp = Number(b.mv5) - Number(a.mv5);
    } else {
      cmp = fcrRank(a) - fcrRank(b);
    }
    return cmp * dir;
  });

  return sorted;
}

export function formatChargerIdsClipboard(labels: string[]): string {
  const lines: string[] = [];
  for (let i = 0; i < labels.length; i += 10) {
    lines.push(labels.slice(i, i + 10).join(", "));
  }
  return lines.join("\n");
}

export function toggleFilter(
  filters: Set<ChargerFilterKey>,
  key: ChargerFilterKey,
): Set<ChargerFilterKey> {
  const next = new Set(filters);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
}

export function toggleSort(
  currentKey: ChargerSortKey,
  currentDir: ChargerSortDir,
  nextKey: ChargerSortKey,
): { key: ChargerSortKey; dir: ChargerSortDir } {
  if (currentKey === nextKey) {
    return { key: nextKey, dir: currentDir === "asc" ? "desc" : "asc" };
  }
  return { key: nextKey, dir: "asc" };
}
